import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import DashboardNav from '../../components/DashboardNav';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Users,
  Wallet,
  Target,
  BarChart3,
  Calendar,
  Briefcase,
  Link2,
  Plus,
  X,
  Loader2,
  Check,
  AlertTriangle,
  Settings2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  functional: { icon: Settings2, color: 'bg-blue-500/10 text-blue-400 border-blue-500/30', label: 'Functional' },
  team: { icon: Users, color: 'bg-purple-500/10 text-purple-400 border-purple-500/30', label: 'Team' },
  domain: { icon: Briefcase, color: 'bg-green-500/10 text-green-400 border-green-500/30', label: 'Domain' },
  process: { icon: Target, color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', label: 'Process' },
  budget: { icon: Wallet, color: 'bg-pink-500/10 text-pink-400 border-pink-500/30', label: 'Budget' }
};

const AreaDetailPage = () => {
  const { areaId } = useParams();
  const navigate = useNavigate();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [areaTypes, setAreaTypes] = useState([]);
  const [decisionAuthorities, setDecisionAuthorities] = useState([]);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchArea();
    fetchTypes();
  }, [areaId]);

  const fetchArea = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/areas/${areaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setArea(data);
        setEditData(data);
      } else if (response.status === 404) {
        navigate('/dashboard/areas');
      }
    } catch (error) {
      console.error('Error fetching area:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/areas/types`);
      if (response.ok) {
        const data = await response.json();
        setAreaTypes(data.types || []);
        setDecisionAuthorities(data.decision_authorities || []);
      }
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      
      // Only include changed fields
      if (editData.name !== area.name) updates.name = editData.name;
      if (editData.type !== area.type) updates.type = editData.type;
      if (editData.scope !== area.scope) updates.scope = editData.scope;
      if (editData.headcount !== area.headcount) updates.headcount = editData.headcount ? parseInt(editData.headcount) : null;
      if (editData.budget_opex !== area.budget_opex) updates.budget_opex = editData.budget_opex ? parseFloat(editData.budget_opex) : null;
      if (editData.reports_to !== area.reports_to) updates.reports_to = editData.reports_to;
      if (JSON.stringify(editData.decision_authority) !== JSON.stringify(area.decision_authority)) {
        updates.decision_authority = editData.decision_authority;
      }
      if (JSON.stringify(editData.stakeholders) !== JSON.stringify(area.stakeholders)) {
        updates.stakeholders = editData.stakeholders;
      }
      if (JSON.stringify(editData.key_metrics) !== JSON.stringify(area.key_metrics)) {
        updates.key_metrics = editData.key_metrics;
      }

      if (Object.keys(updates).length === 0) {
        setEditing(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/dashboard/areas/${areaId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updated = await response.json();
        setArea(updated);
        setEditData(updated);
        setEditing(false);
      }
    } catch (error) {
      console.error('Error saving area:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/areas/${areaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        navigate('/dashboard/areas');
      }
    } catch (error) {
      console.error('Error archiving area:', error);
    }
  };

  const toggleAuthority = (value) => {
    const current = editData.decision_authority || [];
    if (current.includes(value)) {
      setEditData(prev => ({
        ...prev,
        decision_authority: current.filter(a => a !== value)
      }));
    } else {
      setEditData(prev => ({
        ...prev,
        decision_authority: [...current, value]
      }));
    }
  };

  const addMetric = () => {
    setEditData(prev => ({
      ...prev,
      key_metrics: [...(prev.key_metrics || []), { name: '', target: '', current: '' }]
    }));
  };

  const updateMetric = (index, field, value) => {
    const metrics = [...(editData.key_metrics || [])];
    metrics[index] = { ...metrics[index], [field]: value };
    setEditData(prev => ({ ...prev, key_metrics: metrics }));
  };

  const removeMetric = (index) => {
    const metrics = [...(editData.key_metrics || [])];
    metrics.splice(index, 1);
    setEditData(prev => ({ ...prev, key_metrics: metrics }));
  };

  const addStakeholder = (name) => {
    if (name.trim()) {
      setEditData(prev => ({
        ...prev,
        stakeholders: [...(prev.stakeholders || []), name.trim()]
      }));
    }
  };

  const removeStakeholder = (index) => {
    const stakeholders = [...(editData.stakeholders || [])];
    stakeholders.splice(index, 1);
    setEditData(prev => ({ ...prev, stakeholders }));
  };

  const formatBudget = (amount, currency = 'GBP') => {
    if (!amount) return '—';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <DashboardNav />
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (!area) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <DashboardNav />
        <div className="text-center py-12">
          <p className="text-slate-400">Area not found</p>
        </div>
      </div>
    );
  }

  const typeConfig = TYPE_CONFIG[area.type] || TYPE_CONFIG.functional;
  const TypeIcon = typeConfig.icon;

  return (
    <div className="min-h-screen bg-zinc-950">
      <DashboardNav />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6" data-testid="area-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/areas')}
          className="text-slate-400 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Title Section */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${typeConfig.color.split(' ')[0]}`}>
            <TypeIcon className={`w-6 h-6 ${typeConfig.color.split(' ')[1]}`} />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-white">{area.name}</h1>
              {area.status === 'archived' && (
                <Badge variant="outline" className="border-slate-600 text-slate-500">
                  Archived
                </Badge>
              )}
            </div>
            <Badge variant="outline" className={`${typeConfig.color}`}>
              {typeConfig.label}
            </Badge>
            {area.started_at && (
              <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                Since {new Date(area.started_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>
        
        {area.status !== 'archived' && (
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setEditing(false); setEditData(area); }}
                  className="border-slate-700 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEditing(true)}
                  className="border-slate-700 text-slate-300"
                  data-testid="edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Archive
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-800">
                    <DialogHeader>
                      <DialogTitle className="text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        Archive Area
                      </DialogTitle>
                      <DialogDescription className="text-slate-400">
                        Are you sure you want to archive "{area.name}"? You can still view it in your archived areas.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-3 mt-4">
                      <Button variant="outline" onClick={() => setShowArchiveDialog(false)} className="border-slate-700 text-slate-300">
                        Cancel
                      </Button>
                      <Button onClick={handleArchive} className="bg-red-600 hover:bg-red-500" data-testid="confirm-archive">
                        Archive
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Scope */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wide">Scope</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <Textarea
                  value={editData.scope || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, scope: e.target.value }))}
                  className="bg-slate-800/50 border-slate-700 text-white min-h-[100px]"
                  data-testid="edit-scope"
                />
              ) : (
                <p className="text-slate-300 leading-relaxed">{area.scope || '—'}</p>
              )}
            </CardContent>
          </Card>

          {/* Key Metrics */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-slate-400 uppercase tracking-wide flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Key Metrics
                </CardTitle>
                {editing && (
                  <Button size="sm" variant="ghost" onClick={addMetric} className="text-blue-400 hover:text-blue-300">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(editing ? editData.key_metrics : area.key_metrics)?.length > 0 ? (
                <div className="space-y-3">
                  {(editing ? editData.key_metrics : area.key_metrics).map((metric, index) => (
                    <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${editing ? 'bg-slate-800/50' : 'bg-slate-800/30'}`}>
                      {editing ? (
                        <>
                          <Input
                            value={metric.name}
                            onChange={(e) => updateMetric(index, 'name', e.target.value)}
                            placeholder="Metric name"
                            className="flex-1 bg-slate-900/50 border-slate-700 text-white text-sm"
                          />
                          <Input
                            value={metric.target}
                            onChange={(e) => updateMetric(index, 'target', e.target.value)}
                            placeholder="Target"
                            className="w-24 bg-slate-900/50 border-slate-700 text-white text-sm"
                          />
                          <Input
                            value={metric.current}
                            onChange={(e) => updateMetric(index, 'current', e.target.value)}
                            placeholder="Current"
                            className="w-24 bg-slate-900/50 border-slate-700 text-white text-sm"
                          />
                          <Button size="sm" variant="ghost" onClick={() => removeMetric(index)} className="text-slate-500 hover:text-red-400">
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="text-slate-300 flex-1">{metric.name}</span>
                          <span className="text-slate-500 text-sm">Target: {metric.target || '—'}</span>
                          <span className="text-green-400 text-sm font-medium">{metric.current || '—'}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No metrics defined</p>
              )}
            </CardContent>
          </Card>

          {/* Decision Authority */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wide flex items-center gap-2">
                <Target className="w-4 h-4" />
                Decision Authority
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="flex flex-wrap gap-2">
                  {decisionAuthorities.map(auth => (
                    <Badge
                      key={auth.value}
                      variant="outline"
                      className={`cursor-pointer transition-colors ${
                        (editData.decision_authority || []).includes(auth.value)
                          ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                          : 'border-slate-700 text-slate-500 hover:text-slate-300'
                      }`}
                      onClick={() => toggleAuthority(auth.value)}
                    >
                      {auth.label}
                    </Badge>
                  ))}
                </div>
              ) : area.decision_authority?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {area.decision_authority.map(auth => {
                    const authConfig = decisionAuthorities.find(a => a.value === auth);
                    return (
                      <Badge key={auth} variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-300">
                        {authConfig?.label || auth}
                      </Badge>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No authorities defined</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Stats & Links */}
        <div className="space-y-6">
          {/* Scale Card */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wide">Scale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-500 text-xs">Headcount</Label>
                {editing ? (
                  <Input
                    type="number"
                    min="0"
                    value={editData.headcount || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, headcount: e.target.value }))}
                    className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                    data-testid="edit-headcount"
                  />
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <Users className="w-4 h-4 text-slate-500" />
                    <span className="text-white text-lg font-medium">{area.headcount || '—'}</span>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-slate-500 text-xs">Annual Budget (OpEx)</Label>
                {editing ? (
                  <Input
                    type="number"
                    min="0"
                    value={editData.budget_opex || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, budget_opex: e.target.value }))}
                    className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                    data-testid="edit-budget"
                  />
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <Wallet className="w-4 h-4 text-slate-500" />
                    <span className="text-white text-lg font-medium">{formatBudget(area.budget_opex, area.currency)}</span>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-slate-500 text-xs">Reports To</Label>
                {editing ? (
                  <Input
                    value={editData.reports_to || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, reports_to: e.target.value }))}
                    placeholder="e.g., VP Engineering"
                    className="mt-1 bg-slate-800/50 border-slate-700 text-white"
                  />
                ) : (
                  <p className="text-white mt-1">{area.reports_to || '—'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stakeholders */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wide">Stakeholders</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="space-y-2">
                  {(editData.stakeholders || []).map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge variant="outline" className="border-slate-700 text-slate-300 flex-1 justify-between">
                        {s}
                        <X className="w-3 h-3 ml-2 cursor-pointer hover:text-red-400" onClick={() => removeStakeholder(i)} />
                      </Badge>
                    </div>
                  ))}
                  <Input
                    placeholder="Add stakeholder..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        addStakeholder(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="bg-slate-800/50 border-slate-700 text-white text-sm"
                  />
                </div>
              ) : area.stakeholders?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {area.stakeholders.map((s, i) => (
                    <Badge key={i} variant="outline" className="border-slate-700 text-slate-300">{s}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No stakeholders defined</p>
              )}
            </CardContent>
          </Card>

          {/* Linked Items */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wide flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Linked Items
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-slate-500 text-xs mb-1">Projects</p>
                {area.linked_projects?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {area.linked_projects.map((p, i) => (
                      <Badge key={i} variant="outline" className="border-slate-700 text-slate-300 text-xs">{p}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-600 text-sm">None linked</p>
                )}
              </div>
              <div>
                <p className="text-slate-500 text-xs mb-1">People</p>
                {area.linked_people?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {area.linked_people.map((p, i) => (
                      <Badge key={i} variant="outline" className="border-slate-700 text-slate-300 text-xs">{p}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-600 text-sm">None linked</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
        </div>
      </main>
    </div>
  );
};

export default AreaDetailPage;
